import "./App.css";

import NiceModal from "@ebay/nice-modal-react";
import { useContext, useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";

import Header from "components/Header";
import NoticeModal from "components/modals/NoticeModal";
import SimpleModal from "components/modals/SimpleModal";
import ScrollToTop from "components/ScrollToTop";
import SideNav from "components/SideNav";
import { useUser } from "hooks/apis/auth";
import { useHasUnread } from "hooks/apis/notice";
import useAxios from "hooks/useAxios";
import useClearQueryOnLogout from "hooks/useClearQueryOnLogout";
import useSavePreviousUrl from "hooks/useSavePreviousUrl";
import LoginPage from "pages/LoginPage";
import MainPage from "pages/MainPage";
import MonitoringAssistantPage from "pages/MonitoringAssistantPage";
import MonitoringPage from "pages/MonitoringPage";
import MonitoringWorkPage from "pages/MonitoringWorkPage";
import MyInfoPage from "pages/MyInfoPage";
import QnADetailPage from "pages/QnADetailPage";
import QnAListPage from "pages/QnAListPage";
import QnaPage from "pages/QnaPage";
import SettingsAssistantPage from "pages/SettingsAssistantPage";
import SettingsFaqPage from "pages/SettingsFaqPage";
import SettingsFaqWritePage from "pages/SettingsFaqWritePage";
import SettingsGradePage from "pages/SettingsGradePage";
import SettingsNoticePage from "pages/SettingsNoticePage";
import SettingsNoticeWritePage from "pages/SettingsNoticeWritePage";
import SettingsPage from "pages/SettingsPage";
import SettingsPaymentPage from "pages/SettingsPaymentPage";
import { tokenState, userState } from "store/store";

function App() {
  useAxios();
  useUser();
  useClearQueryOnLogout();
  useSavePreviousUrl();
  const hasUnreadMutation = useHasUnread();

  const location = useLocation();
  const user = useRecoilValue(userState);
  const token = useRecoilValue(tokenState);

  const niceModalContext = useContext(NiceModal.NiceModalContext);

  useEffect(() => {
    Object.keys(niceModalContext).forEach((key) => {
      NiceModal.hide(key);
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (token) {
      if (user) {
        if (user.grade.id !== 1) {
          if ((user.reportCount ?? 0) > 3) {
            NiceModal.show(SimpleModal, {
              title: "알림",
              message:
                "작성한 답변이 3회 이상 신고되어 계정이 비활성화 되었습니다. 계정 활성화를 위해 관리자에게 문의해주시기 바랍니다.",
              showCancel: false,
            });
          }
          if (user.grade.id !== 1 && !user.isLinked) {
            NiceModal.show(SimpleModal, {
              title: "알림",
              message:
                "연동이 정상적으로 이뤄지지 않고있습니다. 즉시 관리자에게 문의해주시기바랍니다.",
              showCancel: false,
            });
          }
        }
      }

      if (user?.grade.id !== 1) {
        hasUnreadMutation.mutate(
          {},
          {
            onSuccess: ({ hasUnread }) => {
              if (hasUnread) {
                NiceModal.show(SimpleModal, {
                  title: "알림",
                  message: "확인하지 않은 공지사항이 있습니다.",
                  showCancel: false,
                }).then(() => {
                  NiceModal.show(NoticeModal);
                });
              }
            },
          },
        );
      }
    }
  }, [token]);

  if (!user) {
    return (
      <>
        <Routes>
          <Route path="login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <ScrollToTop />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<MainPage />} />
          <Route path="login" element={<Navigate to="/" replace />} />
          <Route path="qna" element={<QnaPage />}>
            <Route index element={<Navigate to="ongoing" replace />} />
            <Route
              path="ongoing"
              element={
                [1, 2].includes(user.grade.id) ? (
                  <QnAListPage />
                ) : (
                  <QnADetailPage />
                )
              }
            />
            <Route path="completed" element={<QnAListPage />} />
            <Route path="answered" element={<QnAListPage />} />
            {user.grade.id === 1 && (
              <>
                <Route path="transfer" element={<QnAListPage />} />
                <Route path="transfer/:id" element={<QnADetailPage />} />
              </>
            )}
            <Route path=":id" element={<QnADetailPage />} />
          </Route>
          {[1, 2].includes(user.grade.id) && (
            <Route path="monitoring" element={<MonitoringPage />}>
              <Route index element={<Navigate to="assistant" replace />} />
              <Route path="assistant" element={<MonitoringAssistantPage />} />
              <Route path="work/ongoing" element={<MonitoringWorkPage />} />
              <Route path="work/completed" element={<MonitoringWorkPage />} />
              <Route path="work/:id" element={<QnADetailPage />} />
            </Route>
          )}
          {user.grade.id === 1 && (
            <Route path="settings" element={<SettingsPage />}>
              <Route index element={<SettingsAssistantPage />} />
              <Route path="grade" element={<SettingsGradePage />} />
              <Route path="payment" element={<SettingsPaymentPage />} />
              <Route path="notice" element={<SettingsNoticePage />} />
              <Route
                path="notice/write"
                element={<SettingsNoticeWritePage />}
              />
              <Route
                path="notice/:id/edit"
                element={<SettingsNoticeWritePage />}
              />
              <Route path="faq" element={<SettingsFaqPage />} />
              <Route path="faq/write" element={<SettingsFaqWritePage />} />
              <Route path="faq/:id/edit" element={<SettingsFaqWritePage />} />
            </Route>
          )}
          <Route path="my" element={<MyInfoPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <ScrollToTop />
    </>
  );
}

function Layout() {
  return (
    <div className="flex min-h-full h-fit bg-bg">
      <SideNav />
      <div className="w-full pl-[3.5rem] pt-[3.5rem]">
        <Header />
        <Outlet />
      </div>
    </div>
  );
}

export default App;
